<script>
  import { settings, viewStack, recentlySent } from "../../stores";
  import ViewHeader from "../ViewHeader";
  import RecentlySentPreview from "./RecentlySentPreview.svelte";
  import PushNotificationSetting from "../PushNotificationSetting.svelte";
  import ProfileRecoverySetting from "../ProfileRecoverySetting.svelte";
  import DangerZoneSetting from "../DangerZoneSetting.svelte";
  import InboxServerInlineInfo from "../InboxServerInlineInfo.svelte";
  import PopoverList from "../PopoverList.svelte";
  import { updateApp, checkForAppUpdate } from "../../functions/update-app.js";
  import { RELOAD_HANDLING_TYPES, IS_IOS } from "../../constants.js";
  import VersionFooter from "../VersionFooter.svelte";
  import { isInAppWrapper } from "../../functions/app-wrapper-communication.js";
  import pkg from "../../../package.json";

  let newDisplayName = "";
  let userUuid = "";
  let isAppUpdateAvailable = false;
  let previewItem = null;

  $: isDisplayNameSaveable =
    newDisplayName?.trim() !== "" && $settings.displayName !== newDisplayName;

  const version = pkg.version;
  const commit = pkg.gitCommitHash;

  const emailBodyDraft = encodeURIComponent(
    `\n---\nFor debug purposes:\nInbox Server: ${$settings.inboxServerHost}\nApp Version: ${version} ${commit}\nBrowser Info: ${window.navigator.userAgent}`,
  );

  async function saveDisplayName() {
    await settings.set("displayName", newDisplayName);
  }

  function showIdentifier() {
    return window.alert(
      `What makes you you? In snepchat your identity is this:\n\n${userUuid}`,
    );
  }

  async function setup() {
    newDisplayName = $settings.displayName;
    userUuid = await settings.getUserUuid();

    isAppUpdateAvailable = await checkForAppUpdate($settings.isDebugMode);
  }

  setup();
</script>

<ViewHeader showOfflineInfo>
  Profile

  <span slot="secondary">
    <PopoverList
      options={[
        { label: "Show Identifier", handler: showIdentifier },
        { label: "Logout", handler: () => viewStack.push("LogoutProfile") },
      ]}
    />
  </span>
</ViewHeader>

<div class="form -oneline">
  <label for="displayName" class="label">Your Nickname</label>
  <input id="displayName" class="input" bind:value={newDisplayName} />

  <div class="input flex-grow-0">
    {#if isDisplayNameSaveable}
      <button class="-button-primary -button-small" on:click={saveDisplayName}
        >Save</button
      >
    {/if}
  </div>
</div>

{#if isDisplayNameSaveable}
  <p>
    <small
      >Your nickname is only shared when you explicitly create or accept a
      friend invite.</small
    >
  </p>
  <br />
{/if}

<InboxServerInlineInfo />

{#if isAppUpdateAvailable}
  <div class="vertical-empty-space-half" />

  <p>
    <button
      class="-button-primary full-width"
      on:click={() => updateApp(RELOAD_HANDLING_TYPES.FORCE)}
    >
      Update available! Click to update
    </button>
  </p>
{/if}

<div class="vertical-empty-space-half" />

<p>
  <strong>Please note that the app is in development.</strong>
  I look forward to your feedback:
  <a href="mailto:help@snepchat.net?subject=Feedback&body={emailBodyDraft}"
    >help@snepchat.net</a
  >
</p>

<hr />

{#if $recentlySent && $recentlySent.length > 0}
  <div class="display-flex justify-content-space-between align-items-center">
    <h3>Recently Sent</h3>
    <button class="-button-icon" on:click={() => recentlySent.clearAll()} title="Clear Recently Sent">
      <svg class="feather-icon"><use href="feather-sprite.svg#x" /></svg>
    </button>
  </div>
  
  <div class="recently-sent-grid">
    {#each $recentlySent as item}
      <button class="recently-sent-item" on:click={() => previewItem = item}>
        {#if item.type.includes('video')}
          <video src={URL.createObjectURL(item.blob)} muted preload="metadata"></video>
          <div class="video-indicator">
             <svg class="feather-icon"><use href="feather-sprite.svg#video" /></svg>
          </div>
        {:else}
          <img src={URL.createObjectURL(item.blob)} alt="Recently sent" />
        {/if}
      </button>
    {/each}
  </div>
  
  <hr />
{/if}

<h3>Settings</h3>

<PushNotificationSetting />

<ProfileRecoverySetting />

<div class="content-box">
  <header>
    <span>
      <svg class="feather-icon -normalize"><use href="feather-sprite.svg#users"/></svg>
      Friend Groups
    </span>
  </header>
  <p>Group your friends to easily send photos to multiple people at once.</p>
  <p>
    <button class="-button-secondary -button-small full-width" on:click={() => viewStack.push('ManageFriendGroups')}>
      Manage Friend Groups
    </button>
  </p>
</div>

<DangerZoneSetting />

<div class="vertical-empty-space-half" />

<p class="display-flex justify-content-space-between">
  <button
    class="-button-secondary -button-small"
    on:click={() => viewStack.push("Settings")}>Advanced Settings</button
  >

  <button
    class="-button-secondary -button-small"
    on:click={() => viewStack.push("LogoutProfile")}
  >
    Logout

    <svg class="feather-icon"><use href="feather-sprite.svg#log-out" /></svg>
  </button>
</p>

<div class="vertical-empty-space-2x" />

<footer>
  <VersionFooter />

  <div class="vertical-empty-space-half" />

  <p class="text-align-center">
    <button
      class="-button-link"
      on:click={() => viewStack.push("CreditsAndThanks")}>Credits</button
    >
    |
    <button
      class="-button-link"
      on:click={() => viewStack.push("ClientPrivacyPolicy")}
      >Privacy Policy</button
    >
    {#if IS_IOS && !isInAppWrapper()}
      |
      <button
        class="-button-link"
        on:click={() => viewStack.push("IosQuirksExplanation")}
        >Apple iOS FAQ</button
      >
    {/if}
  </p>
</footer>

<RecentlySentPreview visible={!!previewItem} item={previewItem} on:close={() => previewItem = null} />

<style>
  footer,
  footer > p > button {
    font-size: 0.8rem;
  }

  h3 {
    margin-bottom: 1rem;
  }

  .recently-sent-grid {
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;
  }
  
  .recently-sent-item {
    position: relative;
    width: calc(33.333% - 0.66rem);
    aspect-ratio: 9 / 16;
    border-radius: 0.5rem;
    overflow: hidden;
    background: var(--light-gray);
    border: none;
    padding: 0;
    cursor: pointer;
  }

  .recently-sent-item img,
  .recently-sent-item video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .video-indicator {
    position: absolute;
    bottom: 0.5rem;
    right: 0.5rem;
    color: white;
    filter: drop-shadow(0 0 2px rgba(0,0,0,0.5));
  }
</style>
